import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import {
  consumeShareCodeAndFetchRecord,
  normalizeManualCode,
  parseScannedShareCode,
} from 'services/healthRecordsShareService';
import CodeInput from 'components/common/CodeInput';
import QrScanner from './QrScanner';
import SharedRecordSummary from './SharedRecordSummary';

const ConsumeShareCodeDialog = ({ open, onClose, onRecordLoaded }) => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [mode, setMode] = useState('scan');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMode('scan');
    setCode('');
    setError(null);
    setResult(null);
    setLoading(false);
  }, [open]);

  const submitCode = useCallback(
    async (rawCode) => {
      const cleanCode = normalizeManualCode(rawCode);
      if (cleanCode.length !== 7) {
        setError('Enter the full 7-digit code.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const fetched = await consumeShareCodeAndFetchRecord({
          manualCode: cleanCode,
          providerUid: user?.uid,
        });
        setResult(fetched);
        onRecordLoaded?.(fetched);
      } catch (err) {
        setError(err.message || 'Could not validate the code. Try again.');
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, onRecordLoaded],
  );

  const handleScan = useCallback(
    (raw) => {
      const extracted = parseScannedShareCode(raw);
      if (!extracted) {
        setError('That QR code isn’t a HealthDesk share code.');
        return;
      }
      setCode(normalizeManualCode(extracted));
      setMode('code');
      submitCode(extracted);
    },
    [submitCode],
  );

  const digitCount = normalizeManualCode(code).length;

  return (
    // Narrow while scanning, wider once the record is on screen. Full screen on a phone, where
    // the camera viewfinder needs the room. Title with a close button, like SettingsDialog.
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={result ? 'sm' : 'xs'}
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="import-record-title"
    >
      <DialogTitle
        id="import-record-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {result ? 'Patient record' : 'Import patient record'}
        <IconButton aria-label="Close" edge="end" onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!result ? (
          <Stack spacing={2.5} sx={{ pt: 1, pb: 1 }}>
            <Typography variant="body1" color="text.secondary">
              {mode === 'scan'
                ? "Scan the code on your patient's phone."
                : "Enter the 7-digit code from your patient's phone."}
            </Typography>

            {mode === 'scan' ? (
              <QrScanner active={mode === 'scan'} onResult={handleScan} />
            ) : (
              <CodeInput
                value={code}
                onChange={(digits) => {
                  setCode(digits);
                  if (error) setError(null);
                }}
                onComplete={(digits) => {
                  if (!loading) submitCode(digits);
                }}
                error={Boolean(error)}
                disabled={loading}
                autoFocus
                ariaLabel="7-digit code"
              />
            )}

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {mode === 'code' && (
              <LoadingButton
                variant="contained"
                size="large"
                fullWidth
                onClick={() => submitCode(code)}
                loading={loading}
                disabled={digitCount !== 7}
              >
                Import
              </LoadingButton>
            )}

            {/* The other way in, as a quiet switch rather than tabs over the viewfinder. */}
            <Button
              onClick={() => {
                setMode(mode === 'scan' ? 'code' : 'scan');
                setError(null);
              }}
              sx={{ alignSelf: 'center' }}
            >
              {mode === 'scan' ? 'Enter a code instead' : 'Scan a code instead'}
            </Button>
          </Stack>
        ) : (
          // The whole record, not a list of section names: which allergy is the thing on this
          // screen that changes what a clinician can safely prescribe.
          <SharedRecordSummary result={result} />
        )}
      </DialogContent>

      {result && (
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ConsumeShareCodeDialog;
